import { useCallback, useMemo, useState } from "react";
import { matchesSearch } from "@/lib/puzzleFilters";

import { Field, PanelSection, StatusBanner } from "@/components/ui";
import {
  adminApi,
  type AdminPuzzle,
  type AdminPuzzles,
  type PuzzleDraft,
} from "@/lib/adminApi";
import { useApi } from "@/lib/useApi";
import { useSaveState } from "@/lib/useSaveState";

/** Matches the server's allowlist. Stated on the form rather than discovered. */
const BLUEPRINT_HOST = "bp.tali.software";

const BLANK: PuzzleDraft = {
  title: "",
  creator: "",
  archive_difficulty: null,
  puzzle_url: "",
  solution_url: "",
  set_name: "",
  added_on: null,
  is_published: true,
};

function toDraft(puzzle: AdminPuzzle): PuzzleDraft {
  const { id: _id, rating_count: _count, ...draft } = puzzle;
  return draft;
}

/** The editor for one puzzle, whether new or existing. */
function PuzzleForm({
  draft,
  sets,
  busy,
  onChange,
  onSubmit,
  onCancel,
  submitLabel,
}: {
  readonly draft: PuzzleDraft;
  readonly sets: readonly string[];
  readonly busy: boolean;
  readonly onChange: (changes: Partial<PuzzleDraft>) => void;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
  readonly submitLabel: string;
}) {
  return (
    <div className="border-2 border-ink bg-paper px-5 py-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" htmlFor="puzzle-title">
          <input
            id="puzzle-title"
            className="input"
            maxLength={200}
            value={draft.title}
            onChange={(event) => onChange({ title: event.target.value })}
          />
        </Field>

        <Field
          label="Creator"
          htmlFor="puzzle-creator"
          hint="Who made it. Optional."
        >
          <input
            id="puzzle-creator"
            className="input"
            maxLength={64}
            value={draft.creator}
            onChange={(event) => onChange({ creator: event.target.value })}
          />
        </Field>

        <Field
          label="Puzzle link"
          htmlFor="puzzle-url"
          hint={`An https://${BLUEPRINT_HOST} blueprint. Anything else is refused.`}
          className="sm:col-span-2"
        >
          <input
            id="puzzle-url"
            className="input"
            placeholder={`https://${BLUEPRINT_HOST}/?b1@…`}
            value={draft.puzzle_url}
            onChange={(event) => onChange({ puzzle_url: event.target.value })}
          />
        </Field>

        <Field
          label="Solution link"
          htmlFor="solution-url"
          className="sm:col-span-2"
        >
          <input
            id="solution-url"
            className="input"
            placeholder={`https://${BLUEPRINT_HOST}/?b1@…`}
            value={draft.solution_url}
            onChange={(event) => onChange({ solution_url: event.target.value })}
          />
        </Field>

        <Field
          label="Archive difficulty"
          htmlFor="puzzle-difficulty"
          hint="The old spreadsheet number. Leave blank — the club rates it."
        >
          <input
            id="puzzle-difficulty"
            className="input"
            type="number"
            min={1}
            max={30}
            value={draft.archive_difficulty ?? ""}
            onChange={(event) =>
              onChange({
                archive_difficulty: event.target.value
                  ? Number(event.target.value)
                  : null,
              })
            }
          />
        </Field>

        <Field label="Set" htmlFor="puzzle-set" hint="Optional grouping.">
          <input
            id="puzzle-set"
            className="input"
            list="puzzle-set-options"
            maxLength={64}
            value={draft.set_name}
            onChange={(event) => onChange({ set_name: event.target.value })}
          />
          <datalist id="puzzle-set-options">
            {sets.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </Field>
      </div>

      <label className="mt-4 flex items-center gap-2.5 text-[0.9375rem]">
        <input
          type="checkbox"
          checked={draft.is_published}
          onChange={(event) => onChange({ is_published: event.target.checked })}
        />
        Show it in the public archive
      </label>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          className="btn px-4 py-2 text-sm"
          onClick={onSubmit}
          disabled={busy}
        >
          {busy ? "Saving…" : submitLabel}
        </button>
        <button
          type="button"
          className="btn btn--ghost px-4 py-2 text-sm"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * The puzzle archive, editable.
 *
 * Retiring a puzzle is offered as *unpublishing* rather than deleting: a
 * deletion takes every rating of that puzzle with it, and the community
 * difficulty is the thing this whole feature exists to collect. Delete stays
 * available for a puzzle added by mistake, and says what it would destroy.
 */
export function PuzzlesPanel({
  onUnauthenticated,
}: {
  readonly onUnauthenticated: () => void;
}) {
  const [version, setVersion] = useState(0);
  const data = useApi<AdminPuzzles>(() => adminApi.puzzles(), [version]);
  const { saving, message, tone, run } = useSaveState(onUnauthenticated);

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<PuzzleDraft>(BLANK);

  const refresh = useCallback(() => setVersion((current) => current + 1), []);
  const patch = (changes: Partial<PuzzleDraft>) =>
    setDraft({ ...draft, ...changes });

  const puzzles = data.data?.puzzles ?? [];
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? puzzles.filter((p) => matchesSearch(p, needle)) : puzzles;
  }, [puzzles, search]);

  const save = async () => {
    const saved = await run(
      () =>
        editing === "new"
          ? adminApi.addPuzzle(draft)
          : adminApi.updatePuzzle(editing as number, draft),
      (result) => `Saved puzzle ${result.id}.`,
    );
    if (saved) {
      setEditing(null);
      refresh();
    }
  };

  const remove = async (puzzle: AdminPuzzle) => {
    // A delete cascades to every rating of this puzzle, which is the data the
    // whole feature exists to collect and cannot be recovered. The count is on
    // the button, but the button sits beside Edit and Hide — near-identical
    // targets — so the loss gets a second step. ActivitiesPanel confirms a far
    // smaller delete; this one should not be easier.
    const loss =
      puzzle.rating_count > 0
        ? `\n\nThis also deletes ${puzzle.rating_count} community rating${
            puzzle.rating_count === 1 ? "" : "s"
          }, which cannot be recovered. To hide it instead, use "Hide from the archive".`
        : "";
    if (
      !window.confirm(`Delete puzzle ${puzzle.id}, "${puzzle.title}"?${loss}`)
    )
      return;

    const done = await run(
      () => adminApi.deletePuzzle(puzzle.id),
      (r) => r.message,
    );
    if (done) refresh();
  };

  const togglePublished = async (puzzle: AdminPuzzle) => {
    // Re-read before writing. This sends the whole record, so toggling from a
    // list loaded ten minutes ago would quietly restore that record's title and
    // links over anything edited since — by this officer in another tab, or by
    // another officer entirely.
    const done = await run(
      async () => {
        const current = (await adminApi.puzzles()).puzzles.find(
          (p) => p.id === puzzle.id,
        );
        if (!current) throw new Error("That puzzle no longer exists.");
        return adminApi.updatePuzzle(current.id, {
          ...toDraft(current),
          is_published: !current.is_published,
        });
      },
      (result) =>
        result.is_published
          ? `Puzzle ${result.id} is public.`
          : `Puzzle ${result.id} hidden.`,
    );
    if (done) refresh();
  };

  if (data.loading)
    return <p className="meta opacity-60">Loading the archive…</p>;
  if (data.error || !data.data) {
    return (
      <StatusBanner
        tone="error"
        message="The archive could not be loaded."
        prefix={null}
      />
    );
  }

  const archive = data.data;

  return (
    <PanelSection
      title="Puzzles"
      description="The archive behind /puzzles. Everything here is public unless you hide it."
    >
      <StatusBanner tone={tone} message={message} prefix={null} />

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Field
          label="Search"
          htmlFor="admin-puzzle-search"
          className="min-w-[16rem] flex-1"
        >
          <input
            id="admin-puzzle-search"
            className="input"
            placeholder="Title, creator, set, or #number"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
        <button
          type="button"
          className="btn px-4 py-2 text-sm"
          onClick={() => {
            setDraft(BLANK);
            setEditing("new");
          }}
        >
          Add a puzzle
        </button>
      </div>

      {editing === "new" ? (
        <div className="mt-4">
          <p className="meta mb-2 opacity-60">
            It will be number {archive.next_id}.
          </p>
          <PuzzleForm
            draft={draft}
            sets={archive.sets}
            busy={saving}
            onChange={patch}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            submitLabel="Add puzzle"
          />
        </div>
      ) : null}

      <p className="meta mt-5 opacity-60">
        Showing {visible.length} of {puzzles.length}
      </p>

      <div className="mt-2 flex flex-col gap-2">
        {visible.map((puzzle) => (
          <div
            key={puzzle.id}
            className="border-2 border-ink bg-paper px-4 py-3"
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="meta font-mono opacity-50">
                #{String(puzzle.id).padStart(3, "0")}
              </span>
              <span className="font-semibold">{puzzle.title}</span>
              {puzzle.set_name ? (
                <span className="meta border-2 border-ink bg-piece-o px-1.5 py-0.5 text-[0.5625rem]">
                  {puzzle.set_name}
                </span>
              ) : null}
              {!puzzle.is_published ? (
                <span className="meta border-2 border-ink bg-piece-z/25 px-1.5 py-0.5 text-[0.5625rem]">
                  Hidden
                </span>
              ) : null}
              <span className="meta ml-auto opacity-55">
                {puzzle.rating_count === 0
                  ? "no ratings"
                  : `${puzzle.rating_count} rating${puzzle.rating_count === 1 ? "" : "s"}`}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn--ghost px-3 py-1.5 text-xs"
                onClick={() => {
                  setDraft(toDraft(puzzle));
                  setEditing(puzzle.id);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn btn--ghost px-3 py-1.5 text-xs"
                disabled={saving}
                onClick={() => togglePublished(puzzle)}
              >
                {puzzle.is_published
                  ? "Hide from the archive"
                  : "Show in the archive"}
              </button>
              <a
                className="btn btn--ghost px-3 py-1.5 text-xs"
                href={puzzle.puzzle_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open
              </a>
              <button
                type="button"
                className="btn btn--ghost px-3 py-1.5 text-xs"
                disabled={saving}
                onClick={() => remove(puzzle)}
              >
                Delete
                {puzzle.rating_count > 0
                  ? ` (loses ${puzzle.rating_count})`
                  : ""}
              </button>
            </div>

            {editing === puzzle.id ? (
              <div className="mt-4">
                <PuzzleForm
                  draft={draft}
                  sets={archive.sets}
                  busy={saving}
                  onChange={patch}
                  onSubmit={save}
                  onCancel={() => setEditing(null)}
                  submitLabel="Save changes"
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </PanelSection>
  );
}
